import React, { useEffect, useRef, useState, useCallback } from "react";
import { Webcam, GroupedEvent } from "../../types";
import { useTheme } from "./../../hooks/useTheme";
import { Portal } from "../Portal/Portal";
import { formatEventTime } from "../../utils/timeUtils";
import { HapticFeedback } from "../../utils/hapticFeedback";
import styles from "./MarkerPopup.module.css";
import { ConfirmModal } from "./ConfirmModal/ConfirmModal";

import dpsLight1 from "../icons/mapPopup/light/dps1.png";
import dpsLight2 from "../icons/mapPopup/light/dps2.png";
import dpsLight3 from "../icons/mapPopup/light/dps3.png";
import cleanLight1 from "../icons/mapPopup/light/clean1.png";
import cleanLight2 from "../icons/mapPopup/light/clean2.png";
import cleanLight3 from "../icons/mapPopup/light/clean3.png";
import dtpLight1 from "../icons/mapPopup/light/dtp1.png";
import dtpLight2 from "../icons/mapPopup/light/dtp2.png";
import dtpLight3 from "../icons/mapPopup/light/dtp3.png";

import dpsDark1 from "../icons/mapPopup/dark/dps1.png";
import dpsDark2 from "../icons/mapPopup/dark/dps2.png";
import dpsDark3 from "../icons/mapPopup/dark/dps3.png";
import cleanDark1 from "../icons/mapPopup/dark/clean1.png";
import cleanDark2 from "../icons/mapPopup/dark/clean2.png";
import cleanDark3 from "../icons/mapPopup/dark/clean3.png";
import dtpDark1 from "../icons/mapPopup/dark/dtp1.png";
import dtpDark2 from "../icons/mapPopup/dark/dtp2.png";
import dtpDark3 from "../icons/mapPopup/dark/dtp3.png";

import { apiClient } from "../../api/client";
interface MarkerPopupProps {
  isOpen: boolean;
  onClose: () => void;
  markerData: {
    type: "event" | "webcam";
    title: string;
    events?: GroupedEvent[];
    webcam?: Webcam;
    eventCount?: number;
  } | null;
  onClarifyLocation?: (data: {
    eventId?: number;
    locationId?: number;
    title: string;
    coordinates: [number, number];
    initialCoordinates?: [number, number];
  }) => void;
  onShowToast?: (message?: string) => void;
}

export const MarkerPopup: React.FC<MarkerPopupProps> = ({
  isOpen,
  onClose,
  markerData,
  onClarifyLocation,
  onShowToast,
}) => {
  const { theme } = useTheme();
  const sheetRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  // Состояния для свайпа
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);

  // Состояние выбранного события
  const [selectedEventIndex, setSelectedEventIndex] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState<{
    eventIndex: number;
    title: string;
  } | null>(null);
  const [eventVotingType, setEventVotingType] = useState<boolean | null>(null);
  const [locationVotingType, setLocationVotingType] = useState<boolean | null>(
    null
  );
  const [voteStatus, setVoteStatus] = useState<{
    has_vote: boolean;
    vote_type: boolean;
    total_likes: number;
    total_dislikes: number;
  } | null>(null);

  // Блокируем скролл при открытии
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Сброс выбранного события при изменении данных маркера
  useEffect(() => {
    if (markerData?.events && markerData.events.length > 0) {
      const latestEventIndex = markerData.events.reduce(
        (latestIndex, event, currentIndex) => {
          return event.timestamp > markerData.events![latestIndex].timestamp
            ? currentIndex
            : latestIndex;
        },
        0
      );
      setSelectedEventIndex(latestEventIndex);
      setVoteStatus(null);
    } else {
      setSelectedEventIndex(0);
      setVoteStatus(null);
    }
  }, [markerData]);

  useEffect(() => {
    const loadVoteStatus = async () => {
      if (markerData?.events && markerData.events.length > 0) {
        const selectedEvent = markerData.events[selectedEventIndex];

        const locationId = selectedEvent?.currentLocationId;

        if (locationId) {
          try {
            // Загружаем статус голосования по локации для футера
            const response = await apiClient.getLocationVoteStatus(locationId);
            if (response.success && response.data) {
              setVoteStatus(response.data);
            }
          } catch (error) {
            console.error(
              "Ошибка загрузки статуса голосования по локации:",
              error
            );
            setVoteStatus(null);
          }
        }
      }
    };

    if (isOpen && markerData?.type === "event") {
      loadVoteStatus();
    } else {
      setVoteStatus(null);
    }
  }, [isOpen, markerData, selectedEventIndex]);

  const handleLocationVote = useCallback(
    async (voteType: boolean) => {
      const selectedEvent = markerData?.events?.[selectedEventIndex];
      const locationId = selectedEvent?.currentLocationId;

      if (!locationId) {
        console.warn("Нет locationId для голосования");
        onShowToast?.("Ошибка: нет ID локации");
        return;
      }

      if (locationVotingType !== null) {
        return;
      }
      
      // Тактильная обратная связь
      if (voteType) {
        HapticFeedback.success();
      } else {
        HapticFeedback.warning();
      }
      
      setLocationVotingType(voteType);

      try {
        const response = await apiClient.submitLocationVote({
          location_id: locationId,
          vote_type: voteType,
        });

        if (response.success) {
          onShowToast?.("Спасибо за отзыв!");

          // Обновляем статус голосования по локации
          const updatedStatus = await apiClient.getLocationVoteStatus(
            locationId
          );
          if (updatedStatus.success && updatedStatus.data) {
            setVoteStatus(updatedStatus.data);
          }
        }
      } catch (error) {
        console.error("Ошибка голосования за локацию:", error);
        onShowToast?.("Ошибка отправки голоса");
      } finally {
        setLocationVotingType(null);
      }
    },
    [markerData, selectedEventIndex, onShowToast, locationVotingType]
  );

  // Функция для получения иконки события
  const getEventIcon = (eventType: number, locationType: 0 | 1 | 2 = 0) => {
    const icons = {
      light: {
        dps: [dpsLight1, dpsLight2, dpsLight3],
        clean: [cleanLight1, cleanLight2, cleanLight3],
        dtp: [dtpLight1, dtpLight2, dtpLight3],
      },
      dark: {
        dps: [dpsDark1, dpsDark2, dpsDark3],
        clean: [cleanDark1, cleanDark2, cleanDark3],
        dtp: [dtpDark1, dtpDark2, dtpDark3],
      },
    };

    const currentTheme = theme === "dark" ? "dark" : "light";
    let iconSrc = null;
    let altText = "";

    if (eventType === 1) {
      iconSrc = icons[currentTheme].dps[locationType];
      altText = "ДПС";
    } else if (eventType === 2) {
      iconSrc = icons[currentTheme].clean[locationType];
      altText = "Чисто";
    } else if (eventType === 3) {
      iconSrc = icons[currentTheme].dtp[locationType];
      altText = "ДТП";
    }

    if (!iconSrc) return null;

    return <img src={iconSrc} alt={altText} width="36" height="36" />;
  };

  // Универсальная функция голосования
  const handleVote = useCallback(
    async (voteType: boolean) => {
      const voteLabel = voteType ? "актуально" : "неактуально";
      console.log(`🗳️ Голосуем: ${voteLabel}`);

      if (eventVotingType !== null) {
        console.log("⏳ Голосование уже в процессе");
        return;
      }
      setEventVotingType(voteType);

      const selectedEvent = markerData?.events?.[selectedEventIndex];
      if (!selectedEvent?.id) {
        console.warn("⚠️ У события нет ID для голосования");
        onShowToast?.("Ошибка: нет ID события");
        return;
      }

      try {
        const response = await apiClient.submitVote({
          message_id: selectedEvent.id,
          vote_type: voteType,
        });

        if (response.success) {
          console.log(`✅ Голос "${voteLabel}" отправлен успешно`);
          onShowToast?.("Спасибо за отзыв!");

          // Обновляем статус голосования - ПОЛУЧАЕМ АКТУАЛЬНЫЕ ДАННЫЕ С СЕРВЕРА
          // try {
          //   const updatedVoteStatus = await apiClient.getVoteStatus(
          //     selectedEvent.id
          //   );
          //   if (updatedVoteStatus.success && updatedVoteStatus.data) {
          //     setVoteStatus(updatedVoteStatus.data);
          //     console.log(
          //       "✅ Статус голосования обновлен:",
          //       updatedVoteStatus.data
          //     );
          //   }
          // } catch (statusError) {
          //   console.error(
          //     "❌ Ошибка получения обновленного статуса:",
          //     statusError
          //   );
          //   // Fallback - обновляем локально если не удалось получить с сервера
          //   if (voteStatus) {
          //     setVoteStatus({
          //       ...voteStatus,
          //       has_vote: true,
          //       vote_type: voteType,
          //     });
          //   }
          // }
        }
      } catch (error) {
        console.error(`❌ Ошибка отправки голоса "${voteLabel}":`, error);
        onShowToast?.("Ошибка отправки голоса");
      } finally {
        setEventVotingType(null);
      }
    },
    [markerData, selectedEventIndex, onShowToast, eventVotingType]
  );

  const handleIncorrectLocationClick = () => {
    HapticFeedback.medium();
    console.log("Открываем уточнение локации");

    const selectedEvent = markerData?.events?.[selectedEventIndex];
    const locationId = selectedEvent?.currentLocationId;

    const coordinates: [number, number] = [
      selectedEvent?.currentPoint?.longitude || selectedEvent?.longitude || 0,
      selectedEvent?.currentPoint?.latitude || selectedEvent?.latitude || 0,
    ];

    const dataToSend = {
      eventId: selectedEvent?.id,
      locationId: locationId,
      title: markerData?.title || "Локация",
      coordinates: coordinates,
      initialCoordinates: coordinates, // Передаем исходные координаты для отслеживания
    };

    onClarifyLocation?.(dataToSend);
  };

  // Свайп функциональность
  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
    setIsDragging(true);

    if (sheetRef.current) {
      sheetRef.current.style.transition = "none";
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;

    e.preventDefault();

    const touchY = e.touches[0].clientY;
    const deltaY = touchY - startY;

    setCurrentY(touchY);

    if (deltaY > 0) {
      if (sheetRef.current) {
        sheetRef.current.style.transform = `translateY(${deltaY}px)`;
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);

    if (sheetRef.current) {
      sheetRef.current.style.transition = "transform 0.3s ease-out";
    }

    const deltaY = currentY - startY;

    if (deltaY > 120) {
      onClose();
    } else {
      if (sheetRef.current) {
        sheetRef.current.style.transform = "translateY(0)";
      }
    }

    setStartY(0);
    setCurrentY(0);
  };

  // Глобальные обработчики свайпа
  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, startY, currentY]);

  const renderEventMessage = (event: GroupedEvent) => {
    if (event.replies && event.replies.length > 0) {
      const sortedReplies = [...event.replies].sort(
        (a, b) => a.Level - b.Level
      );

      return (
        <div className={styles.eventMessage}>
          {sortedReplies.map((reply) => (
            <React.Fragment key={reply.ID}>
              <i>{reply.Message} → </i>
            </React.Fragment>
          ))}
          {event.message}
        </div>
      );
    }

    return <div className={styles.eventMessage}>{event.message}</div>;
  };

  const renderEvents = () => {
    if (!markerData || !markerData.events || markerData.events.length === 0)
      return null;

    const selectedEvent = markerData.events[selectedEventIndex];
    const isLocationButtonDisabled = !selectedEvent?.id || selectedEvent.id < 0;

    return (
      <>
        <div className={styles.header}>
          <h3 className={styles.title}>{markerData.title}</h3>
          {markerData.eventCount && (
            <div className={styles.eventCount}>
              {markerData.eventCount}{" "}
              {markerData.eventCount === 1 ? "отметка" : "отметки"}
            </div>
          )}
        </div>

        <div className={styles.eventsList}>
          {markerData.events.map((event, index) => (
            <div key={`${event.timestamp}-${index}`}>
              <div
                className={`${styles.eventItem} ${
                  index === selectedEventIndex ? styles.selectedEvent : ""
                }`}
                //onClick={() => setSelectedEventIndex(index)}
              >
                <div className={styles.eventIcon}>
                  {getEventIcon(event.type, (event.currentLocation?.type ?? 0) as 0 | 1 | 2)}
                </div>
                <div className={styles.eventContent}>
                  {renderEventMessage(event)}
                  <div className={styles.eventMeta}>
                    <span className={styles.eventTime}>
                      {formatEventTime(event.timestamp)}
                    </span>
                    <span className={styles.dotSeparator}>•</span>
                    <span className={styles.eventUser}>{event.from}</span>
                  </div>
                </div>

                {/* Кнопка с тремя точками */}
                <button
                  className={styles.eventMenuButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmModalData({
                      eventIndex: index,
                      title: `Сообщение «${event.message}» не относится к указанной локации?`,
                    });
                    setShowConfirmModal(true);
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="5" cy="12" r="2.25" fill="currentColor" />
                    <circle cx="12" cy="12" r="2.25" fill="currentColor" />
                    <circle cx="19" cy="12" r="2.25" fill="currentColor" />
                  </svg>
                </button>

                {/* {index === selectedEventIndex && (
                  <div className={styles.selectedIndicator}>
                    <svg
                      className={styles.checkIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )} */}
              </div>
              {index < markerData.events!.length - 1 && (
                <div className={styles.separator} />
              )}
            </div>
          ))}
        </div>

        {/* Футер с кнопками - внизу и всегда виден */}
        <div className={styles.footer}>
          <div className={styles.actionButtonsRow}>
            <button
              className={`${styles.voteButton} ${
                voteStatus?.has_vote && voteStatus?.vote_type === true
                  ? styles.voteButtonLikeActive
                  : styles.voteButtonLikeInactive
              }`}
              onClick={() => handleLocationVote(true)}
              disabled={locationVotingType !== null}
            >
              {locationVotingType === true ? (
                <div className={styles.spinner}></div>
              ) : (
                <>
                  <svg
                    width="22"
                    height="19"
                    viewBox="0 0 22 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.5 8.75H2C1.80109 8.75 1.61032 8.82902 1.46967 8.96967C1.32902 9.11032 1.25 9.30109 1.25 9.5V17.75C1.25 17.9489 1.32902 18.1397 1.46967 18.2803C1.61032 18.421 1.80109 18.5 2 18.5H6.5M6.5 8.75V18.5M6.5 8.75L10.25 1.25C11.0456 1.25 11.8087 1.56607 12.3713 2.12868C12.9339 2.69129 13.25 3.45435 13.25 4.25V6.5H19.25C19.4628 6.50006 19.6731 6.54539 19.867 6.63297C20.0609 6.72056 20.234 6.8484 20.3747 7.008C20.5154 7.1676 20.6206 7.35532 20.6832 7.55868C20.7458 7.76204 20.7644 7.97639 20.7378 8.1875L19.6128 17.1875C19.5672 17.5499 19.3908 17.8832 19.1169 18.1249C18.843 18.3665 18.4903 18.4999 18.125 18.5H6.5"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                  <div className={styles.voteCount}>
                    {voteStatus?.total_likes || 0}
                  </div>
                </>
              )}
            </button>

            <button
              className={`${styles.voteButton} ${
                voteStatus?.has_vote && voteStatus?.vote_type === false
                  ? styles.voteButtonDislikeActive
                  : styles.voteButtonDislikeInactive
              }`}
              onClick={() => handleLocationVote(false)}
              disabled={locationVotingType !== null}
            >
              {locationVotingType === false ? (
                <div className={styles.spinner}></div>
              ) : (
                <>
                  <svg
                    width="22"
                    height="19"
                    viewBox="0 0 22 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.5 0.5H2C1.80109 0.5 1.61032 0.579018 1.46967 0.71967C1.32902 0.860322 1.25 1.05109 1.25 1.25V9.5C1.25 9.69891 1.32902 9.88968 1.46967 10.0303C1.61032 10.171 1.80109 10.25 2 10.25H6.5M6.5 0.5V10.25M6.5 0.5H18.125C18.4903 0.500103 18.843 0.633492 19.1169 0.87514C19.3908 1.11679 19.5672 1.45009 19.6128 1.8125L20.7378 10.8125C20.7644 11.0236 20.7458 11.238 20.6832 11.4413C20.6206 11.6447 20.5154 11.8324 20.3747 11.992C20.234 12.1516 20.0609 12.2794 19.867 12.367C19.6731 12.4546 19.4628 12.4999 19.25 12.5H13.25V14.75C13.25 15.5456 12.9339 16.3087 12.3713 16.8713C11.8087 17.4339 11.0456 17.75 10.25 17.75L6.5 10.25"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                  <div className={styles.voteCount}>
                    {voteStatus?.total_dislikes || 0}
                  </div>
                </>
              )}
            </button>

            <button
              className={styles.incorrectLocationButton}
              onClick={handleIncorrectLocationClick}
              disabled={isLocationButtonDisabled}
            >
              <svg
                width="21"
                height="18"
                viewBox="0 0 21 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.58252 1.01953C9.98093 0.326689 11.019 0.326743 11.4175 1.01953V1.02051L19.6167 15.2578C19.989 15.9087 19.52 16.7498 18.6997 16.75H2.30029C1.48014 16.7497 1.01116 15.9087 1.3833 15.2578L9.58252 1.02051V1.01953Z"
                  stroke="#8C8A92"
                  fill="none"
                  strokeLinecap="round"
                />

                <path
                  d="M10.5 6V10M10.5 12.5V13"
                  stroke="#8C8A92"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Неверная локация
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderWebcam = () => {
    if (!markerData || !markerData.webcam) return null;

    return (
      <>
        <div className={styles.webcamHeader}>
          <div className={styles.webcamHeaderContent}>
            <h3 className={styles.webcamTitle}>{markerData.webcam.name}</h3>
          </div>
        </div>

        <div className={styles.videoContainer}>
          <iframe
            src={markerData.webcam.url}
            className={styles.videoFrame}
            frameBorder="0"
            allowFullScreen
            title={`Веб-камера ${markerData.webcam.name}`}
          />
        </div>
      </>
    );
  };

  const handleConfirmIncorrectLocation = () => {
    if (confirmModalData && markerData?.events) {
      handleVote(false);
    }
  };

  if (!isOpen || !markerData) {
    // Сбрасываем состояния при закрытии

    return null;
  }

  return (
    <Portal>
      <div className={styles.overlay}>
        <div ref={sheetRef} className={styles.sheetContainer}>
          <button
            className={styles.backButton}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div
            className={`${styles.sheet} ${
              markerData.type === "webcam"
                ? styles.webcamSheet
                : styles.eventsSheet
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              ref={handleRef}
              className={styles.swipeHandle}
              onTouchStart={handleTouchStart}
            >
              <div className={styles.swipeIndicator} />
            </div>

            <div className={styles.content}>
              {markerData.type === "event" && renderEvents()}
              {markerData.type === "webcam" && renderWebcam()}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmIncorrectLocation}
        title={confirmModalData?.title || ""}
      />
    </Portal>
  );
};
